/**
 * -----------------------------------------------------------------------------
 * NCJ COMPONENT: Multi-VIN Cart Extension
 * FILE: ncj-cart-multi-vin.js
 * PURPOSE: Handles dynamic VIN inputs in the cart for splitting items, intercepts change events, and performs batch updates.
 * AUTHOR: No Code Jungle (NCJ)
 * LAST MODIFIED: 2026-02-26
 * -----------------------------------------------------------------------------
 */

document.addEventListener('DOMContentLoaded', () => {
    initCartVinLogic();
});

// Debounce guard: prevents initCartVinLogic from firing more than once per 500ms
// to avoid Bold pr.js mutation-loop (Bold calls /cart/update.js → our interceptor
// modifies DOM → Bold MutationObserver fires again → loop → 429).
let ncjInitPending = false;
function scheduleInitCartVinLogic(delay) {
    if (ncjInitPending) return;
    ncjInitPending = true;
    setTimeout(() => {
        ncjInitPending = false;
        initCartVinLogic();
    }, delay || 300);
}

// React to cart updates (like showing drawer) via Dawn's PubSub
if (window.subscribe && window.PUB_SUB_EVENTS) {
    window.subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
        scheduleInitCartVinLogic(300);
    });
}

// Global interceptor for Dawn's cart re-rendering (e.g., after standard remove).
// Only watches /cart/change (Dawn item qty changes / remove) — NOT /cart/update,
// which Bold pr.js uses for B2B price overrides. Watching /cart/update caused
// a mutation loop: Bold updates → our DOM change → Bold MutationObserver fires → loop → 429.
// The PubSub subscriber above already handles re-init after any cart update cycle.
const originalFetch = window.fetch;
window.fetch = async (...args) => {
    const response = await originalFetch(...args);
    if (args[0] && typeof args[0] === 'string' && args[0].includes('/cart/change')) {
        const clone = response.clone();
        clone.text().then(() => {
            scheduleInitCartVinLogic(800); // Wait for Dawn DOM swap
        }).catch(err => console.error("Error intercepting cart change:", err));
    }
    return response;
};

// Also listen for a generic cart:updated custom event used by many themes
document.addEventListener('cart:updated', () => {
    scheduleInitCartVinLogic(300);
});

function initCartVinLogic() {
    const cartItems = document.querySelectorAll('.cart-item');
    if (!cartItems || cartItems.length === 0) return;

    cartItems.forEach(item => {
        const properties = item.querySelectorAll('.product-option');
        let isVinItem = false;
        let existingVin = "";

        properties.forEach(prop => {
            const dt = prop.querySelector('dt');
            const dd = prop.querySelector('dd');
            if (dt && dt.innerText.trim().toUpperCase() === 'VIN:') {
                isVinItem = true;
                if (dd) existingVin = dd.innerText.trim();
            }
        });

        if (isVinItem) {
            setupVinCartItem(item, existingVin);
        }
    });

    // We also hide standard cart remove buttons for VIN items to force them to use Qty 0 if they want to remove? 
    // Actually the standard remove button calls updateQuantity(index, 0). 
    // We should intercept that too, but since cart update reloads anyway, 
    // maybe we just let standard remove work for single items. 
    // Wait, standard remove bypasses our batch logic and does an immediate ajax to remove. This is fine, they just remove 1 item.

    // Recalculate Update Cart button state after every cart re-render
    // (handles product removal leaving the button orphaned)
    checkCartUpdates();
}

function setupVinCartItem(itemRow, existingVin) {
    const qtyInput = itemRow.querySelector('.quantity__input');
    if (!qtyInput || qtyInput.dataset.ncjInitialized) return;

    qtyInput.dataset.ncjInitialized = "true";

    // Insert container for extra VINs
    const detailsTd = itemRow.querySelector('.cart-item__details');
    if (!detailsTd) return;

    const vinContainer = document.createElement('div');
    vinContainer.className = 'ncj-cart-vins-wrapper';

    // Create a special TD for mobile grid-spanning (to avoid HTML parsing hoist)
    const mobileVinCell = document.createElement('td');
    mobileVinCell.className = 'ncj-cart-vin-mobile-cell';
    itemRow.appendChild(mobileVinCell);

    const mql = window.matchMedia('(max-width: 749px)');

    const placeVinContainer = (isMobile) => {
        if (isMobile) {
            mobileVinCell.appendChild(vinContainer);
            mobileVinCell.style.display = 'block'; // Ensure grid participation
        } else {
            detailsTd.appendChild(vinContainer);
            mobileVinCell.style.display = 'none'; // Hide empty block
        }
    };

    // Initial placement
    placeVinContainer(mql.matches);

    // Listen for resize to swap placement dynamically
    mql.addEventListener('change', (e) => placeVinContainer(e.matches));

    // Delegación de eventos en fase de captura (true) para bloquear propagación hacia Dawn
    // Esencial para interceptar eventos que no hacen burbuja como 'blur'.
    ['change', 'input', 'paste', 'focusout', 'blur'].forEach(evt => {
        vinContainer.addEventListener(evt, (e) => {
            if (e.target && e.target.classList && e.target.classList.contains('ncj-vin-input')) {
                // Ensure normalization happens even if propagation is stopped
                if (evt === 'input' && window.NCJUtils && window.NCJUtils.normalizeVin) {
                    e.target.value = window.NCJUtils.normalizeVin(e.target.value);
                }
                e.stopPropagation();
            }
        }, true);
    });

    // Initial base states
    // A specific line item with a VIN is always quantity 1 natively, but lets check data-cart-quantity
    let currentQty = parseInt(qtyInput.value) || 1;
    qtyInput.dataset.initialQty = currentQty;

    // Obtener los botones de cantidad
    const btnMinus = itemRow.querySelector('button[name="minus"]');
    const btnPlus = itemRow.querySelector('button[name="plus"]');

    const updateButtonStates = (qty) => {
        if (btnMinus) {
            if (qty <= 1) {
                btnMinus.setAttribute('disabled', 'disabled');
                btnMinus.classList.add('disabled');
            } else {
                btnMinus.removeAttribute('disabled');
                btnMinus.classList.remove('disabled');
            }
        }
        if (btnPlus) {
            if (qty >= 10) {
                btnPlus.setAttribute('disabled', 'disabled');
                btnPlus.classList.add('disabled');
            } else {
                btnPlus.removeAttribute('disabled');
                btnPlus.classList.remove('disabled');
            }
        }
    };

    updateButtonStates(currentQty);

    // Intercept quantity changes and BLOCK Dawn's auto-update
    const preventDawn = (e) => {
        // Bloqueo total de eventos para que Dawn no detecte cambios químicos o manuales
        e.stopPropagation();
        e.stopImmediatePropagation();

        let newQty = parseInt(e.target.value) || 0;
        if (newQty > 10) newQty = 10;
        e.target.value = newQty;

        updateButtonStates(newQty);

        if (newQty !== currentQty) {
            handleCartQtyChange(itemRow, vinContainer, currentQty, newQty);
            currentQty = newQty;
            checkCartUpdates();
        }
    };

    // Escuchamos en fase de captura para ser los primeros en bloquear
    qtyInput.addEventListener('change', preventDawn, { capture: true });
    qtyInput.addEventListener('input', preventDawn, { capture: true });

    // Algunos temas disparan actualización al perder el foco (blur/focusout)
    qtyInput.addEventListener('focusout', (e) => {
        e.stopPropagation();
        e.stopImmediatePropagation();
    }, { capture: true });
}

function handleCartQtyChange(itemRow, container, oldQty, newQty) {
    const existingInputs = container.querySelectorAll('.ncj-vin-field-group');
    const existingCount = existingInputs.length;

    // Since original line is Qty 1 conceptually, extra needed is newQty - 1
    const targetExtra = newQty > 0 ? newQty - 1 : 0;

    if (targetExtra > existingCount) {
        for (let i = existingCount + 1; i <= targetExtra; i++) {
            const group = window.NCJUtils.createVinFieldGroup({
                label: '', // No label as requested
                id: `ncj-cart-vin-${Date.now()}-${i}`,
                showIcon: true,
                customClasses: 'ncj-cart-extra-vin ncj-vin-full-width'
            });

            container.appendChild(group);
        }
    } else if (targetExtra < existingCount) {
        for (let i = existingCount; i > targetExtra; i--) {
            container.removeChild(container.lastElementChild);
        }
    }
}

function checkCartUpdates() {
    const qtyInputs = document.querySelectorAll('.cart-item .quantity__input[data-ncj-initialized="true"]');
    let hasChanges = false;

    qtyInputs.forEach(input => {
        if (parseInt(input.value) !== parseInt(input.dataset.initialQty)) {
            hasChanges = true;
        }
    });

    const checkoutBtn = document.querySelector('#checkout') || document.querySelector('button[name="checkout"]');
    const updateBtnRow = document.getElementById('ncj-cart-update-btn-row');

    if (hasChanges) {
        // Bloquear checkout si hay cambios sin guardar
        if (checkoutBtn) {
            checkoutBtn.disabled = true;
            checkoutBtn.classList.add('disabled');
            checkoutBtn.style.opacity = '0.5';
            checkoutBtn.style.pointerEvents = 'none';
        }

        if (!updateBtnRow) {
            const btnRow = document.createElement('div');
            btnRow.id = 'ncj-cart-update-btn-row';
            btnRow.className = 'ncj-update-cart-row';

            const updateText = window.vinCheckTranslations?.updateCart || "Update Cart";

            const updateBtn = document.createElement('button');
            updateBtn.className = 'button button--primary ncj-update-cart-btn';
            updateBtn.id = 'ncj-submit-cart-updates';
            updateBtn.textContent = updateText;
            btnRow.appendChild(updateBtn);

            const cartDiv = document.querySelector('cart-items .page-width') || document.querySelector('.cart-drawer__inner');
            if (cartDiv) {
                cartDiv.appendChild(btnRow);
            } else {
                document.body.appendChild(btnRow);
            }

            updateBtn.addEventListener('click', performBatchCartUpdate);
        } else {
            updateBtnRow.style.display = 'flex';
        }
    } else {
        if (updateBtnRow) updateBtnRow.style.display = 'none';
        // Rehabilitar checkout si no hay cambios
        if (checkoutBtn) {
            checkoutBtn.disabled = false;
            checkoutBtn.classList.remove('disabled');
            checkoutBtn.style.opacity = '1';
            checkoutBtn.style.pointerEvents = 'auto';
        }
    }
}

async function performBatchCartUpdate() {
    const submitBtn = document.getElementById('ncj-submit-cart-updates');

    // 1. Validate
    let hasError = false;
    const cartDivs = document.querySelectorAll('.ncj-cart-vins-wrapper');
    cartDivs.forEach(div => {
        const validation = window.NCJUtils.validateVinContainer(div);
        if (!validation.isValid) hasError = true;
    });

    if (hasError) return;

    submitBtn.textContent = "Updating...";
    submitBtn.disabled = true;

    try {
        const shopifyRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ? window.Shopify.routes.root : '/';
        const resCart = await fetch(shopifyRoot + 'cart.js');
        if (!resCart.ok) throw new Error("Failed to fetch cart.js (Status: " + resCart.status + ")");
        const cartState = await resCart.json();
        const cartItemsRows = document.querySelectorAll('.cart-item');
        const updates = {};
        const itemsToAdd = [];

        cartItemsRows.forEach(row => {
            const qtyInput = row.querySelector('.quantity__input');
            if (!qtyInput) return;

            const lineIndex = parseInt(qtyInput.dataset.index);
            if (isNaN(lineIndex) || !cartState.items[lineIndex - 1]) return;

            const lineKey = cartState.items[lineIndex - 1].key;
            const newQty = parseInt(qtyInput.value) || 0;

            if (qtyInput.dataset.ncjInitialized) {
                // VIN Item logic
                if (newQty === 0) {
                    updates[lineKey] = 0;
                } else {
                    updates[lineKey] = 1; // force base to 1
                    const extraVinInputs = row.querySelectorAll('.ncj-cart-extra-vin .ncj-vin-input');
                    const originalLineItem = cartState.items[lineIndex - 1];
                    const baseProperties = originalLineItem.properties || {};

                    const extraVinInputsArray = Array.from(extraVinInputs).reverse();
                    extraVinInputsArray.forEach(input => {
                        let finalProps = {};
                        if (baseProperties && typeof baseProperties === 'object' && !Array.isArray(baseProperties)) {
                            finalProps = JSON.parse(JSON.stringify(baseProperties));
                        }
                        // Force uppercase and normalization before sending
                        const cleanVin = window.NCJUtils.normalizeVin(input.value.trim());
                        finalProps["VIN"] = cleanVin;

                        itemsToAdd.push({
                            id: originalLineItem.variant_id,
                            quantity: 1,
                            properties: finalProps
                        });
                    });
                }
            } else {
                // Standard uninitialized item, just copy current input value
                updates[lineKey] = newQty;
            }
        });

        // 2. Perform updates for existing lines
        if (Object.keys(updates).length > 0) {
            const resUpdate = await fetch(shopifyRoot + 'cart/update.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ updates: updates })
            });
            if (!resUpdate.ok) {
                const errTxt = await resUpdate.text();
                if (resUpdate.status === 429 || errTxt.trim().indexOf("<!DOCTYPE") === 0 || errTxt.trim().indexOf("<html") === 0) {
                    throw new Error("Temporary block by Shopify. Please refresh the page.");
                }
                throw new Error("Update failed: " + resUpdate.status + " " + errTxt);
            }
        }

        // 3. Perform adds for split lines
        if (itemsToAdd.length > 0) {
            const resAdd = await fetch(shopifyRoot + 'cart/add.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ items: itemsToAdd })
            });
            if (!resAdd.ok) {
                const errTxt = await resAdd.text();
                if (resAdd.status === 429 || errTxt.trim().indexOf("<!DOCTYPE") === 0 || errTxt.trim().indexOf("<html") === 0) {
                    throw new Error("Temporary block by Shopify. Please refresh the page.");
                }
                throw new Error("Add failed: " + resAdd.status + " " + errTxt);
            }
        }

        // 4. Reload to visually flush state cleanly
        window.location.reload();

    } catch (err) {
        console.error("Cart update error:", err);
        alert("Server Error: " + err.message);
        submitBtn.textContent = "Error: " + err.message.substring(0, 15) + "...";
        submitBtn.disabled = false;
    }
}
